/** Resource kinds that have a view screen in the mobile app (aligned with getResourceRoute). */
export type AppResourceType =
    | 'animation'
    | 'supply'
    | 'mission'
    | 'formation'
    | 'antenna'
    | 'missionType'
    | 'expensesClaim'
    | 'highlightedResource'
    | 'member';

export type AppResourceContext = {
    /** Required for `missionType` app links (mission listing filtered by antenna). */
    antennaId?: string;
};

const DEFAULT_APP_SCHEME = 'wwf-benevolat-app';

const requireAntennaId = (
    type: AppResourceType,
    antennaId: string | undefined
): string => {
    if (!antennaId) {
        throw new Error(`antennaId is required for ${type} app links`);
    }
    return antennaId;
};

/**
 * Expo-router path that opens a resource in the mobile app.
 * Keep in sync with mobile `getResourceRoute`.
 */
export const getAppPath = (
    type: AppResourceType,
    id: string,
    context?: AppResourceContext
): string => {
    switch (type) {
        case 'antenna':
            return `/antennas/${id}/view`;
        case 'animation':
            return `/resources/animations/${id}/view`;
        case 'supply':
            return `/resources/supplies/${id}/view`;
        case 'expensesClaim':
            return `/resources/expenses-claim/${id}/view`;
        case 'highlightedResource':
            return `/resources`;
        case 'formation':
            return `/resources/formations/${id}/view`;
        case 'mission':
            return `/missions/${id}/view`;
        case 'missionType': {
            const antennaId = requireAntennaId(type, context?.antennaId);
            return `/missions/listing/by-antenna?antennaId=${antennaId}&filterType=future&missionTypeId=${id}`;
        }
        case 'member': {
            return `/members/${id}/view`;
        }
        default: {
            const _exhaustive: never = type;
            return _exhaustive;
        }
    }
};

/**
 * Custom-scheme URL that opens a resource in the mobile app (same shape as expo-linking createURL).
 */
export const getAppDeepLink = (
    type: AppResourceType,
    id: string,
    context?: AppResourceContext
): string => {
    const scheme =
        import.meta.env.VITE_APP_SCHEME?.trim() || DEFAULT_APP_SCHEME;
    const path = getAppPath(type, id, context).replace(/^\//, '');
    if (scheme.startsWith('http')) {
        return `${scheme}/${path}`;
    }
    return `${scheme}://${path}`;
};
